import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import logo from '@assets/654global.svg';
import { Button } from '@components/Button/Button';
import Image from '@components/Image/Image';
import { Input } from '@components/Input/Input';
import { EStyles } from '@enums/style.enum';
import useApi from '@hooks/useApi';
import useTripSocket from '@hooks/useTripSocket';
import { setError, setFlightNumber, setIsLoading } from '@store/auth/authSlice';
import type { RootState } from '@store/store';
import { isExpired } from '@utils/isExpired';

import classes from './Search.module.scss';

const Search = () => {
	const [value, setValue] = useState<string>(EStyles.emptyQuote);

	const token = useSelector((state: RootState) => state.auth.token);
	const error = useSelector((state: RootState) => state.auth.error);
	const isLoading = useSelector((state: RootState) => state.auth.isLoading);

	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { getTrips } = useApi();
	const { connectTripsSocket } = useTripSocket();

	const handleSubmit = () => {
		if (!value) {
			return dispatch(setError(t('search.searchErrors.empty')));
		} else if (!token || isExpired(token)) {
			return dispatch(setError(t('search.searchErrors.noValidLinkOrExpired')));
		}
		dispatch(setError(undefined));
		dispatch(setIsLoading(true));
		dispatch(setFlightNumber(value));
		getTrips(value);
		connectTripsSocket();
	};

	return (
		<div className={classes['container']}>
			<div className={classes['formContainer']}>
				<div className={classes['headerContainer']}>
					<h1 className={classes['title']}>{t('search.title')}</h1>
					<h2 className={classes['subtitle']}>{t('search.subtitle')}</h2>
				</div>
				<div className={classes['form']}>
					<Input
						numbersAndLettersOnly
						placeholder={t('search.placeholder')}
						value={value}
						onTextChange={(value: string) => setValue(value)}
						inputMode={EStyles.text}
						autoCapitalize={EStyles.none}
						maxLength={8}
						error={error}
					/>
					<Button fullWidth primary uppercase isLoading={isLoading} onClick={handleSubmit}>
						{t('search.button')}
					</Button>
				</div>
			</div>
			<div className={classes['imageContainer']}>
				<p className={classes['text']}>{t('search.slug')}</p>
				<Image src={logo} name={t('search.imgAlt')} size="60vw" />
			</div>
		</div>
	);
};

export default Search;
